/**
 * Homepage About Me content.
 *
 * Stored on the published website content as homepageAbout, separate from the
 * About page, which continues to use heroDescription and aboutDescription.
 * When homepageAbout has not been published, the homepage still shows Carmen's
 * existing introduction.
 */

export interface AboutPortrait {
  imageUrl: string;
  publicId: string;
  altText: string;
  width?: number;
  height?: number;
}

export interface FocalPoint {
  x: number;
  y: number;
}

export interface HomepageAboutStored {
  showPortrait?: boolean;
  portrait?: AboutPortrait | null;
  desktopPosition?: FocalPoint;
  mobilePosition?: FocalPoint;
  eyebrow?: string;
  heading?: string;
  introduction?: string;
  showButton?: boolean;
  buttonLabel?: string;
  buttonUrl?: string;
}

export interface HomepageAboutDraft {
  showPortrait: boolean;
  portrait: AboutPortrait | null;
  desktopPosition: FocalPoint;
  mobilePosition: FocalPoint;
  eyebrow: string;
  heading: string;
  introduction: string;
  showButton: boolean;
  buttonLabel: string;
  buttonUrl: string;
}

export interface ResolvedHomepageAbout extends HomepageAboutDraft {
  hasPortrait: boolean;
  introductionHtml: string;
}

export const HOMEPAGE_ABOUT_SUGGESTED = {
  eyebrow: 'A little about me',
  heading: "Hello, I'm Carmen",
  buttonLabel: 'Get to know me',
  buttonUrl: '/about',
};

const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'NOSCRIPT', 'LINK', 'META']);
const ALLOWED_TAGS = new Set(['P', 'BR', 'STRONG', 'EM', 'B', 'I', 'U', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'A']);

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function clampFocal(value: unknown): number {
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number)) return 50;
  return Math.min(100, Math.max(0, Math.round(number)));
}

export function normalizeFocal(value: Partial<FocalPoint> | null | undefined): FocalPoint {
  return {
    x: clampFocal(value?.x),
    y: clampFocal(value?.y),
  };
}

export function isHttpUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

export function isAcceptableButtonUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 500) return false;
  if (trimmed.startsWith('/') && !trimmed.startsWith('//') && !trimmed.startsWith('/\\')) return true;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'https:' || url.protocol === 'http:' || url.protocol === 'mailto:' || url.protocol === 'tel:';
  } catch {
    return false;
  }
}

export function normalizePortrait(value: unknown): AboutPortrait | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Partial<AboutPortrait>;
  const imageUrl = String(record.imageUrl || '').trim();
  if (!isHttpUrl(imageUrl)) return null;
  const width = Number(record.width);
  const height = Number(record.height);
  const altText = String(record.altText || '').trim() || 'Carmen Lorraine';
  return {
    imageUrl,
    publicId: String(record.publicId || '').trim(),
    altText,
    ...(Number.isFinite(width) && width > 0 ? { width } : {}),
    ...(Number.isFinite(height) && height > 0 ? { height } : {}),
  };
}

function plainTextToHtml(text: string): string {
  return text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

export function fallbackIntroduction(hero?: string, about?: string): string {
  return [hero, about]
    .map((part) => plainTextToHtml(part || ''))
    .filter(Boolean)
    .join('');
}

export function introductionText(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function isSafeHref(value: string): boolean {
  return isAcceptableButtonUrl(value);
}

function serializeChildren(node: ParentNode): string {
  let html = '';
  node.childNodes.forEach((child) => {
    html += serializeNode(child);
  });
  return html;
}

function serializeNode(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return escapeHtml(node.textContent || '');
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return '';
  const element = node as HTMLElement;
  const tag = element.tagName;
  if (DROP_TAGS.has(tag)) return '';
  if (tag === 'BR') return '<br>';
  if (tag === 'DIV' || tag === 'SPAN') {
    const inner = serializeChildren(element);
    if (tag === 'SPAN') return inner;
    if (!inner.trim()) return '';
    if (/^<(p|ul|ol|blockquote|div)\b/i.test(inner.trim())) return inner;
    return `<p>${inner}</p>`;
  }
  if (!ALLOWED_TAGS.has(tag)) return serializeChildren(element);
  const inner = serializeChildren(element);
  if (tag === 'A') {
    const href = (element.getAttribute('href') || '').trim();
    if (!isSafeHref(href)) return inner;
    return `<a href="${escapeHtml(href)}">${inner}</a>`;
  }
  const tagName = tag.toLowerCase();
  if ((tag === 'P' || tag === 'LI' || tag === 'BLOCKQUOTE') && !introductionText(inner) && !/<br\b/i.test(inner)) {
    return '';
  }
  return `<${tagName}>${inner}</${tagName}>`;
}

export function sanitizeIntroduction(input: string): string {
  const raw = (input || '').trim();
  if (!raw) return '';
  const source = /<[a-z][\s\S]*>/i.test(raw) ? raw : plainTextToHtml(raw);
  if (typeof DOMParser === 'undefined') {
    return escapeHtml(raw.replace(/<[^>]+>/g, ''));
  }
  const doc = new DOMParser().parseFromString(`<div>${source}</div>`, 'text/html');
  return serializeChildren(doc.body).trim();
}

export function safeButtonUrl(value: string | undefined): string {
  const trimmed = (value || '').trim();
  if (isAcceptableButtonUrl(trimmed)) return trimmed;
  return HOMEPAGE_ABOUT_SUGGESTED.buttonUrl;
}

interface WebsiteAboutSource {
  heroDescription?: string;
  aboutDescription?: string;
  homepageAbout?: HomepageAboutStored | null;
}

export function createHomepageAboutDraft(content: WebsiteAboutSource): HomepageAboutDraft {
  const stored = content.homepageAbout;
  const hasStored = !!stored;
  return {
    showPortrait: stored?.showPortrait === true,
    portrait: normalizePortrait(stored?.portrait),
    desktopPosition: normalizeFocal(stored?.desktopPosition),
    mobilePosition: normalizeFocal(stored?.mobilePosition),
    eyebrow: hasStored && stored?.eyebrow !== undefined ? stored.eyebrow : HOMEPAGE_ABOUT_SUGGESTED.eyebrow,
    heading: hasStored && stored?.heading !== undefined ? stored.heading : HOMEPAGE_ABOUT_SUGGESTED.heading,
    introduction: hasStored && stored?.introduction !== undefined
      ? stored.introduction
      : fallbackIntroduction(content.heroDescription, content.aboutDescription),
    showButton: false,
    buttonLabel: hasStored && stored?.buttonLabel !== undefined ? stored.buttonLabel : HOMEPAGE_ABOUT_SUGGESTED.buttonLabel,
    buttonUrl: hasStored && stored?.buttonUrl !== undefined ? stored.buttonUrl : HOMEPAGE_ABOUT_SUGGESTED.buttonUrl,
  };
}

export function resolveHomepageAbout(content: WebsiteAboutSource, draft?: HomepageAboutDraft | null): ResolvedHomepageAbout {
  const source = draft || createHomepageAboutDraft(content);
  const portrait = normalizePortrait(source.portrait);
  const showPortrait = source.showPortrait === true;
  return {
    ...source,
    portrait,
    showPortrait,
    hasPortrait: showPortrait && !!portrait,
    desktopPosition: normalizeFocal(source.desktopPosition),
    mobilePosition: normalizeFocal(source.mobilePosition),
    introductionHtml: sanitizeIntroduction(source.introduction),
    buttonUrl: safeButtonUrl(source.buttonUrl),
    buttonLabel: source.buttonLabel,
    showButton: false,
  };
}

export function validateHomepageAbout(draft: HomepageAboutDraft): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];
  if (!introductionText(draft.introduction)) {
    errors.push('Add an introduction before saving.');
  }
  if (draft.showButton) {
    if (!draft.buttonLabel.trim()) {
      errors.push('Add a button label, or hide the button.');
    }
    if (!isAcceptableButtonUrl(draft.buttonUrl)) {
      errors.push('Use a site path such as /about, or a full http(s) address.');
    }
  }
  if (draft.showPortrait && !draft.portrait) {
    warnings.push('Show portrait is on, but no photograph is selected. The homepage will use the text-only layout until you choose one.');
  }
  return { errors, warnings };
}
